// client/src/App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';

import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';

import Homepage from './Homepage';
import MapView from './views/MapView';
import MyMaps from './views/MyMaps';
import UserMaps from './views/UserMaps';
import ProfileModal from './components/ProfileModal';
import FavouritesPage from './views/FavouritesPage';
import EmbedMapView from './views/EmbedMapView';
import MyComments from './views/MyComments';
import OfflineMapView from './views/OfflineMapView';

import PrivacyPolicy from './components/PrivacyPolicy.js';

import DndMapView from './dnd/views/MapView';
import EmitterTool from './dnd/emitter_tool/EmitterTool';

import ComsApp from './coms/views/ComsApp';
import VoxemeraHomeView from './coms/views/VoxemeraHomeView';
import CanvasStandaloneView from './coms/views/CanvasStandaloneView';
import { AUTHORITY_ORIGIN } from './coms/comsApi';

function RedirectOfflineToLocal() {
  const { id } = useParams();
  return <Navigate to={`/local-map/${id}`} replace />;
}

function RedirectOfflineMapToLocal() {
  const { id } = useParams();
  return <Navigate to={`/local-map/${id}`} replace />;
}

function InviteRedirect() {
  const { code } = useParams();
  const raw = String(code || '').trim();
  const target = raw ? `/comms/?invite=${encodeURIComponent(raw)}` : '/comms/';
  return <Navigate to={target} replace />;
}

function RedirectComsAlias() {
  return <Navigate to="/comms/" replace />;
}

function isVoxemeraHost() {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = String(window.location.host || '').toLowerCase();
  return host.endsWith('voxemera.com');
}

function buildAuthorityUrl(path) {
  const base = AUTHORITY_ORIGIN || '';
  if (!base) return path;
  if (!path) return base;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!path.startsWith('/')) return `${base}/${path}`;
  return `${base}${path}`;
}

function buildLocalUrl(path) {
  if (!path) return '/';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (!path.startsWith('/')) return `/${path}`;
  return path;
}

async function fetchJsonWithCredsUrl(url) {
  try {
    const res = await fetch(url, { credentials: 'include', cache: 'no-store' });
    if (!res.ok) return null;
    const data = await res.json().catch(() => null);
    return data || null;
  } catch {
    return null;
  }
}

async function fetchJsonWithCreds(path, opts) {
  const allowAuthorityFallback = !!(opts && opts.allowAuthorityFallback);

  const localUrl = buildLocalUrl(path);
  const local = await fetchJsonWithCredsUrl(localUrl);
  if (local) return local;

  if (allowAuthorityFallback && AUTHORITY_ORIGIN && AUTHORITY_ORIGIN.length) {
    const authorityUrl = buildAuthorityUrl(path);
    const auth = await fetchJsonWithCredsUrl(authorityUrl);
    if (auth) return auth;
  }

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    try {
      window.scrollTo(0, 0);
    } catch {}
  }, [pathname]);

  return null;
}

function safeJsonParse(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function AppWrapper() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [user, setUser] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileUser, setProfileUser] = useState(null);
  const lastUserIdRef = useRef(null);

  const refreshUser = useCallback(async () => {
    const allowAuthorityFallback = !isVoxemeraHost();

    const u1 = await fetchJsonWithCreds('/api/me', { allowAuthorityFallback });
    if (u1 && (u1.id != null || u1.user_id != null)) {
      // Signing in as a different account (e.g. "Add another account" in a popup) restarts Voxemera so
      // nothing from the previous account stays on screen or connected.
      const uid = u1.id != null ? u1.id : u1.user_id;
      if (lastUserIdRef.current != null && String(lastUserIdRef.current) !== String(uid) && window.location.pathname.startsWith('/comms')) {
        window.location.assign('/comms');
        return u1;
      }
      lastUserIdRef.current = uid;
      setUser(u1);
      return u1;
    }

    const u2 = await fetchJsonWithCreds('/api/user/me', { allowAuthorityFallback });
    if (u2 && (u2.id != null || u2.user_id != null)) {
      setUser(u2);
      return u2;
    }

    setUser(null);
    return null;
  }, []);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('config', 'G-PGFCHW8NZY', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);

  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'username_taken') {
      setShowUsernameModal(true);
      navigate(location.pathname, { replace: true });
    }
  }, [searchParams, location.pathname, navigate]);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    function onMessage(ev) {
      const allowedOrigins = new Set([
        window.location.origin,
        'https://point-atlas.io',
        'https://beta.point-atlas.io',
        'https://voxemera.com',
        'https://beta.voxemera.com',
      ]);

      if (!allowedOrigins.has(String(ev.origin || ''))) return;

      const raw = ev && ev.data != null ? ev.data : null;
      if (!raw) return;

      let data = null;

      if (typeof raw === 'string') {
        data = safeJsonParse(raw);
      } else if (typeof raw === 'object') {
        data = raw;
      } else {
        return;
      }

      if (!data || typeof data !== 'object') return;

      const type = String(data.type || '').trim();

      const ok =
        type === 'oauth-success' ||
        type === 'PA_OAUTH_SUCCESS' ||
        type === 'oauth_success' ||
        type === 'OAUTH_SUCCESS';

      if (!ok) return;

      setTimeout(() => {
        refreshUser();
      }, 200);
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refreshUser]);

  const openProfile = useCallback((u) => {
    setProfileUser(u || null);
    setProfileOpen(true);
  }, []);

  const closeProfile = useCallback(() => {
    setProfileOpen(false);
    setProfileUser(null);
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await fetch('/auth/logout', { method: 'GET', credentials: 'include' });
    } catch {}
    setUser(null);
  }, []);

  const rootElement = isVoxemeraHost() ? (
    <VoxemeraHomeView user={user} setUser={setUser} onLogout={onLogout} />
  ) : (
    <Homepage user={user} setUser={setUser} openProfile={openProfile} onLogout={onLogout} />
  );

  return (
    <>
      <ScrollToTop />

      {profileOpen ? (
        <ProfileModal
          open={profileOpen}
          onClose={closeProfile}
          user={profileUser}
          currentUser={user}
          refreshUser={refreshUser}
        />
      ) : null}

      {showUsernameModal && user ? (
        <ProfileModal
          open={showUsernameModal}
          user={user}
          currentUser={user}
          initialEditing
          initialStatusMessage="Username already taken. Please choose a new username."
          onClose={() => setShowUsernameModal(false)}
          onUserUpdate={(updatedUser) => {
            setUser(updatedUser);
            setShowUsernameModal(false);
          }}
          refreshUser={refreshUser}
        />
      ) : null}

      <Routes>
        <Route path="/" element={rootElement} />

        <Route path="/privacy" element={<PrivacyPolicy />} />

        <Route path="/invite/:code" element={<InviteRedirect />} />
        <Route path="/invite/:code/*" element={<InviteRedirect />} />

        <Route path="/comms/*" element={<ComsApp user={user} setUser={setUser} onLogout={onLogout} />} />
        <Route path="/coms/*" element={<RedirectComsAlias />} />

        <Route path="/map/:id" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:id/*" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:id/:name" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:id/:name/*" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:slug_:username_:id" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:mapId/:name" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:mapId/:name/*" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/map/:mapId" element={<MapView user={user} setUser={setUser} openProfile={openProfile} />} />

        <Route path="/offline/:id" element={<RedirectOfflineToLocal />} />
        <Route path="/offline-map/:id" element={<RedirectOfflineMapToLocal />} />
        <Route path="/local-map/:id" element={<OfflineMapView user={user} setUser={setUser} />} />

        <Route path="/my-maps" element={<MyMaps user={user} setUser={setUser} />} />

        <Route path="/user/:id" element={<UserMaps user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/user/id/:id/maps" element={<UserMaps user={user} setUser={setUser} openProfile={openProfile} />} />
        <Route path="/user/:username/maps" element={<UserMaps user={user} setUser={setUser} openProfile={openProfile} />} />

        <Route path="/favourites" element={<FavouritesPage user={user} setUser={setUser} />} />
        <Route path="/my-comments" element={<MyComments user={user} />} />

        <Route path="/embed/:id" element={<EmbedMapView user={user} />} />
        <Route path="/embed/map/:mapId" element={<EmbedMapView user={user} />} />

        <Route path="/dnd/map/:id" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/dnd/map/:id/*" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/dnd/emitter/:id" element={<EmitterTool user={user} setUser={setUser} />} />

        <Route path="/vtt/:id" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:id/*" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:id/:name" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:id/:name/*" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:slug_:username_:id" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:mapId/:name" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:mapId/:name/*" element={<DndMapView user={user} setUser={setUser} />} />
        <Route path="/vtt/:mapId" element={<DndMapView user={user} setUser={setUser} />} />

        <Route path="/dnd/emitter/:id/*" element={<EmitterTool user={user} setUser={setUser} />} />
        <Route path="/emitter-tool" element={<EmitterTool user={user} setUser={setUser} />} />

        <Route path="/canvas/*" element={<CanvasStandaloneView user={user} />} />

        <Route path="*" element={rootElement} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppWrapper />
    </Router>
  );
}

export default App;